import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Download, Loader2, FileSpreadsheet, RefreshCw, Clock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import {
  listExports,
  createCsvExport,
  getExportDownloadUrl,
} from "@/lib/exports.functions";
import { listMyWorkspaces } from "@/lib/workspaces.functions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/settings/exports")({
  component: ExportsPage,
});

const TYPE_LABELS: Record<string, string> = {
  contacts: "Contacts",
  transactions: "Transactions",
  state_transitions: "State transitions",
  push_queue: "Push queue history",
};

const RANGE_OPTIONS = [
  { label: "All time", value: "all" },
  { label: "Last 7 days", value: "7" },
  { label: "Last 30 days", value: "30" },
  { label: "Last 90 days", value: "90" },
];

function ExportsPage() {
  const listFn = useServerFn(listExports);
  const createFn = useServerFn(createCsvExport);
  const urlFn = useServerFn(getExportDownloadUrl);
  const wsFn = useServerFn(listMyWorkspaces);
  const qc = useQueryClient();

  const [type, setType] = useState("contacts");
  const [workspaceId, setWorkspaceId] = useState<string>("all");
  const [range, setRange] = useState("all");

  const { data, isLoading } = useQuery({
    queryKey: ["exports"],
    queryFn: () => listFn({}),
    refetchInterval: 10_000,
  });

  const { data: wsData } = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => wsFn({}),
  });

  const create = useMutation({
    mutationFn: () =>
      createFn({
        data: {
          export_type: type as any,
          workspace_id: workspaceId === "all" ? null : workspaceId,
          filters: range === "all" ? {} : { since_days: Number(range) },
        },
      }),
    onSuccess: (r) => {
      toast.success(`Exported ${r.row_count.toLocaleString()} rows`);
      qc.invalidateQueries({ queryKey: ["exports"] });
      if (r.url) window.open(r.url, "_blank");
    },
    onError: (e: any) => toast.error(e.message ?? "Export failed"),
  });

  async function download(exportId: string) {
    try {
      const { url } = await urlFn({ data: { export_id: exportId } });
      window.open(url, "_blank");
    } catch (e: any) {
      toast.error(e.message ?? "Failed to get download URL");
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">CSV Exports</h2>
        <p className="text-sm text-muted-foreground">
          Generate CSV downloads of your contacts, transactions, lifecycle changes,
          and CRM push history. Files are stored privately for 7 days.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <FileSpreadsheet className="h-4 w-4 text-primary" />
          <h3 className="font-medium text-sm">New export</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-medium mb-1 block">Data</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_LABELS).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">Workspace</label>
            <Select value={workspaceId} onValueChange={setWorkspaceId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All workspaces</SelectItem>
                {(wsData?.workspaces ?? []).map((w: any) => (
                  <SelectItem key={w.id} value={w.id}>{w.program_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">Range</label>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {RANGE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              className="w-full"
              onClick={() => create.mutate()}
              disabled={create.isPending}
            >
              {create.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>
              ) : (
                <><Download className="h-4 w-4 mr-2" /> Generate CSV</>
              )}
            </Button>
          </div>
        </div>
        {type === "contacts" || type === "push_queue" ? (
          <p className="mt-3 text-xs text-muted-foreground">
            Range filter applies only to transactions, state transitions, and push queue exports.
          </p>
        ) : null}
      </div>

      <div className="rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border p-4">
          <h3 className="font-medium text-sm">Recent exports</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => qc.invalidateQueries({ queryKey: ["exports"] })}
          >
            <RefreshCw className="h-3 w-3 mr-1" /> Refresh
          </Button>
        </div>
        {isLoading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
          </div>
        ) : !data?.exports?.length ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No exports yet. Generate your first CSV above.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {data.exports.map((e: any) => (
              <div key={e.id} className="flex items-center justify-between p-4 gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{TYPE_LABELS[e.export_type] ?? e.export_type}</span>
                    <StatusBadge status={e.status} />
                    {e.workspace_id ? (
                      <Badge variant="outline" className="text-xs">workspace</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">all workspaces</Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {e.row_count != null && <>{e.row_count.toLocaleString()} rows · </>}
                    {e.file_size_bytes != null && <>{formatBytes(e.file_size_bytes)} · </>}
                    requested {formatDistanceToNow(new Date(e.requested_at), { addSuffix: true })}
                    {e.error_message ? <> · <span className="text-destructive">{e.error_message}</span></> : null}
                  </div>
                </div>
                {e.status === "completed" && (
                  <Button size="sm" variant="outline" onClick={() => download(e.id)}>
                    <Download className="h-3 w-3 mr-1" /> Download
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === "completed") {
    return (
      <Badge variant="secondary" className="text-xs gap-1">
        <CheckCircle2 className="h-3 w-3 text-emerald-500" /> ready
      </Badge>
    );
  }
  if (status === "failed") {
    return (
      <Badge variant="destructive" className="text-xs gap-1">
        <XCircle className="h-3 w-3" /> failed
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs gap-1">
      <Clock className="h-3 w-3" /> {status}
    </Badge>
  );
}

function formatBytes(b: number) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}
